import { DialogOpenEvent } from '@theme/dialog';

/**
 * The "Más de [n] personas compraron hoy" number in the cart drawer.
 *
 * A new number within the element's data-min/data-max range is picked every time the cart
 * drawer opens. The drawer re-renders its markup from the server on each cart change, which
 * would put back the server's value, so each element re-applies the current number whenever its
 * text is replaced. The number therefore only changes when the drawer is opened again.
 */

/** @type {number | null} */
let currentCount = null;

/**
 * @param {HTMLElement} element
 * @returns {number}
 */
function pickCount(element) {
  const min = Number.parseInt(element.dataset.min ?? '', 10) || 1;
  const max = Math.max(min, Number.parseInt(element.dataset.max ?? '', 10) || min);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

class KatuaTodayCount extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    if (currentCount === null) currentCount = pickCount(this);
    this.apply();

    this.#observer = new MutationObserver(() => this.apply());
    this.#observer.observe(this, { childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /**
   * Shows the current number (no-op when it is already shown, so the observer does not loop).
   */
  apply() {
    const text = String(currentCount);
    if (this.textContent !== text) this.textContent = text;
  }
}

if (!customElements.get('katua-today-count')) {
  customElements.define('katua-today-count', KatuaTodayCount);
}

// The dialog's open event does not bubble, so listen in the capture phase.
document.addEventListener(
  DialogOpenEvent.eventName,
  (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('cart-drawer-component')) return;

    const counters = Array.from(document.querySelectorAll('katua-today-count')).filter(
      (element) => element instanceof KatuaTodayCount
    );
    const [first] = counters;
    if (!first) return;

    currentCount = pickCount(first);
    for (const counter of counters) counter.apply();
  },
  true
);
